import React, { useMemo } from "react";

/* ────────────────────────────────────────────────────────────────────────────
 * Starfield, fixed background twinkling stars layer
 * Tiny SVG dots scattered with random CSS keyframe twinkle delays.
 * Use behind a parent that establishes a stacking context.
 * ──────────────────────────────────────────────────────────────────────────── */
export default function Starfield({
  density = 60,
  color = "#FFE9A8",
  glow = "rgba(255, 222, 130, 0.85)",
  className = "",
  fixed = true,
  intensity = 1,
}) {
  const stars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < density; i++) {
      const size = Math.random() * 2 + 0.6; // 0.6 to 2.6px
      arr.push({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size,
        opacity: 0.35 + Math.random() * 0.65,
        delay: Math.random() * 6,
        duration: 2.2 + Math.random() * 3.4,
        blur: size > 1.8 ? 0.6 : 0,
      });
    }
    return arr;
  }, [density]);

  // A handful of "big" sparkle stars (4-point) for extra magic
  const bigSparkles = useMemo(() => {
    const arr = [];
    const count = Math.max(4, Math.floor(density / 12));
    for (let i = 0; i < count; i++) {
      arr.push({
        id: i,
        left: 5 + Math.random() * 90,
        top: 5 + Math.random() * 90,
        size: 6 + Math.random() * 10,
        delay: Math.random() * 5,
        duration: 3 + Math.random() * 3,
      });
    }
    return arr;
  }, [density]);

  return (
    <div
      aria-hidden="true, "
      className={`${fixed ? "fixed" : "absolute"} inset-0 pointer-events-none overflow-hidden ${className}`}
      style={{ zIndex: 0, opacity: intensity }}
    >
      {/* dot stars */}
      {stars.map((s) => (
        <span
          key={`d-${s.id}`}
          className="absolute rounded-full"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            backgroundColor: color,
            opacity: s.opacity,
            filter: `drop-shadow(0 0 ${4 + s.size}px ${glow}) ${s.blur ? `blur(${s.blur}px)` : ""}`,
            animation: `star-twinkle ${s.duration}s ease-in-out ${s.delay}s infinite`,
          }}
        />
      ))}

      {/* big 4-point sparkles */}
      {bigSparkles.map((s) => (
        <svg
          key={`b-${s.id}`}
          className="absolute"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            filter: `drop-shadow(0 0 8px ${glow}) drop-shadow(0 0 14px ${glow})`,
            animation: `sparkle-pulse ${s.duration}s ease-in-out ${s.delay}s infinite`,
          }}
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z"
            fill={color}
          />
        </svg>
      ))}

      <style>{`
        @keyframes star-twinkle {
          0%, 100% { opacity: 0.15; transform: scale(0.85); }
          50% { opacity: 1; transform: scale(1.25); }
        }
        @keyframes sparkle-pulse {
          0%, 100% { opacity: 0.25; transform: rotate(0deg) scale(0.7); }
          50% { opacity: 1; transform: rotate(180deg) scale(1.15); }
        }
      `}</style>
    </div>
  );
}
